import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Section, Container } from '../../ui';
import styles from './BrandShowcase.module.css';

interface BrandData {
  name: string;
  refs: string;
}

const BRANDS: BrandData[] = [
  { name: 'Apple', refs: '18 000+' },
  { name: 'Samsung', refs: '14 000+' },
  { name: 'Xiaomi', refs: '4 500+' },
  { name: 'Lenovo', refs: '5 200+' },
  { name: 'HP', refs: '4 800+' },
  { name: 'Dell', refs: '3 600+' },
  { name: 'Huawei', refs: '2 800+' },
  { name: 'Sony', refs: '2 100+' },
];

const SECONDARY_BRANDS = [
  'Nokia', 'Motorola', 'Google', 'OnePlus', 'Honor',
  'Oppo', 'vivo', 'Realme', 'Nothing', 'Fairphone',
  'Crosscall', 'DORO', 'Wiko', 'BlackBerry', 'HTC',
  'TECNO', 'TCL', 'Asus', 'Acer', 'LG',
];

export function BrandShowcase() {
  const { t } = useTranslation('catalogue');

  const [isVisible, setIsVisible] = useState(false);
  const hasAnimated = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const wrapperClass = `${styles.wrapper}${isVisible ? ` ${styles.visible}` : ''}`;

  return (
    <Section variant="light">
      <Container>
        <div ref={wrapperRef} className={wrapperClass}>
          <div className={styles.header}>
            <div className={styles.badge}>
              <span className={styles.badgeText}>{t('brands.badge')}</span>
            </div>
            <h2 className={styles.title}>{t('brands.title')}</h2>
            <p className={styles.subtitle}>{t('brands.subtitle')}</p>
          </div>

          <div className={styles.grid}>
            {BRANDS.map((brand, i) => (
              <div
                key={brand.name}
                className={styles.card}
                style={{ '--card-index': i } as React.CSSProperties}
              >
                <img
                  src={`/images/brands/${brand.name.toLowerCase()}.svg`}
                  alt={brand.name}
                  className={styles.logo}
                  loading="lazy"
                />
                <span className={styles.brandName}>{brand.name}</span>
                <span className={styles.count}>{brand.refs} {t('brands.refs')}</span>
              </div>
            ))}
          </div>

          <div className={styles.secondaryRow}>
            {SECONDARY_BRANDS.map((name, i) => (
              <span
                key={name}
                className={styles.secondaryBrand}
                style={{ '--brand-index': i } as React.CSSProperties}
              >
                {name}
              </span>
            ))}
          </div>

          <p className={styles.moreCount}>{t('brands.moreCount')}</p>
        </div>
      </Container>
    </Section>
  );
}
